import type { GameState } from '@rsvjelle/game-core';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? (isNativeApp() ? 'https://rs.vjelle.be' : import.meta.env.DEV ? 'http://localhost:8787' : '')).replace(/\/$/, '');
const DEVICE_KEY = 'rsv2.device-id';
const NATIVE_TOKEN_KEY = 'rsv2.native-token';

export interface CloudUser { id: string; username: string; deviceId: string; email: string | null }
export interface CloudSave { revision: number; state: GameState | null; updatedAt?: number; updatedByDevice?: string }

export class CloudApiError extends Error {
  constructor(public readonly code: string, message: string, public readonly status: number, public readonly details?: unknown) {
    super(message);
  }
}

export function deviceId(): string {
  let value = localStorage.getItem(DEVICE_KEY);
  if (!value) { value = crypto.randomUUID(); localStorage.setItem(DEVICE_KEY, value); }
  return value;
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const nativeToken = localStorage.getItem(NATIVE_TOKEN_KEY);
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(nativeToken ? { Authorization: `Bearer ${nativeToken}` } : {}), ...init.headers }
  });
  const body = await response.json().catch(() => ({})) as Record<string, any>;
  if (!response.ok) throw new CloudApiError(body.error?.code ?? 'request_failed', body.error?.message ?? 'Cloud request failed.', response.status, body.conflict);
  return body as T;
}

export function currentUser(): Promise<CloudUser> { return call('/v1/auth/me'); }
function isNativeApp(): boolean {
  return Boolean((window as Window & { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.());
}

async function authenticate(path: string, identifier: string, password: string, email?: string): Promise<{ username: string; accessToken?: string }> {
  const native = isNativeApp();
  const result = await call<{ username: string; accessToken?: string }>(path, {
    method: 'POST', body: JSON.stringify({ username: identifier, password, email, deviceId: deviceId(), clientType: native ? 'native' : 'browser' })
  });
  if (native && result.accessToken) localStorage.setItem(NATIVE_TOKEN_KEY, result.accessToken);
  return result;
}
export function register(username: string, email: string, password: string): Promise<{ username: string; accessToken?: string }> { return authenticate('/v1/auth/register', username, password, email); }
export function login(identifier: string, password: string): Promise<{ username: string; accessToken?: string }> { return authenticate('/v1/auth/login', identifier, password); }
export async function logout(): Promise<void> {
  try { await call('/v1/auth/logout', { method: 'POST', body: '{}' }); }
  finally { localStorage.removeItem(NATIVE_TOKEN_KEY); }
}
export function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  return call('/v1/auth/password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) });
}
export function updateRecoveryEmail(email: string, password: string): Promise<{ email: string }> {
  return call('/v1/auth/email', { method: 'POST', body: JSON.stringify({ email, password }) });
}
export function requestPasswordRecovery(identifier: string): Promise<void> {
  return call('/v1/auth/recovery/request', { method: 'POST', body: JSON.stringify({ username: identifier }) });
}
export function resetPassword(token: string, newPassword: string): Promise<void> {
  return call('/v1/auth/recovery/reset', { method: 'POST', body: JSON.stringify({ token, newPassword }) });
}
export async function deleteCloudAccount(password: string): Promise<void> {
  await call('/v1/account', { method: 'DELETE', body: JSON.stringify({ password }) });
  localStorage.removeItem(NATIVE_TOKEN_KEY);
}
export function acquireLease(force = false): Promise<{ expiresAt: number }> {
  return call('/v1/sync/lease', { method: 'POST', body: JSON.stringify({ deviceId: deviceId(), force }) });
}
export function getCloudSave(): Promise<CloudSave> { return call('/v1/save'); }
export function putCloudSave(state: GameState, baseRevision: number): Promise<{ revision: number }> {
  return call('/v1/save', {
    method: 'PUT',
    body: JSON.stringify({ state, baseRevision, deviceId: deviceId(), requestId: crypto.randomUUID() })
  });
}
